import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment, createContext, memo, useContext, useMemo } from "react";
import { blockManifestReader } from "../internal/read-block-manifest.js";
import { BlockErrorBoundary } from "../../utils/BlockErrorBoundary.js";
import { InlineEditingContextProvider, ReadonlyInlineEditingProvider } from "./inline-editing.js";
import { addBlockMetadata } from "./block-utils.js";
import { useRouter } from "../routing/index.js";
export const BlocksContext = createContext(undefined);
/**
 * Render a set of Gutenberg blocks
 */
export const ContentBlocks = memo((props) => {
    const blockData = props.blocks;
    if (!Array.isArray(blockData))
        return null;
    const parentContext = useContext(BlocksContext);
    const blocks = useMemo(() => {
        addBlockMetadata(blockData);
        return blockData.map((block, index) => {
            // Create a context object for the current block
            const ctx = {
                ...parentContext,
                ancestors: [...(parentContext?.ancestors || []), parentContext?.current].filter(Boolean),
                parent: parentContext?.current,
                current: block,
                prev: blockData[index - 1],
                next: blockData[index + 1],
                readonly: env.admin,
                index: index,
            };
            // Figure out the
            let blockNode;
            blockNode = _jsx(SingleContentBlock, { block: block, index: index });
            return [ctx, block, blockNode];
        });
    }, [blockData]);
    const layout = useMemo(() => {
        return blocks.filter(Boolean).map(([ctx, block, blockNode], index) => {
            // Exit early if the block is null
            if (blockNode === null)
                return _jsx(Fragment, {}, index);
            // Custom wrap function?
            if (props.wrapBlock) {
                blockNode = props.wrapBlock(blockNode, ctx);
            }
            // Wrap the block in a context provider
            blockNode = _jsx(BlocksContext.Provider, { value: ctx, children: blockNode });
            // Custom spacer function?
            let spaceBefore = null;
            let spaceAfter = null;
            if (props.spacer) {
                // Before block
                spaceBefore = props.spacer({
                    ...ctx,
                    prev: ctx.prev || null,
                    next: ctx.current || null,
                });
                // After block (last block only)
                if (index === blockData.length - 1) {
                    spaceAfter = props.spacer({
                        ...ctx,
                        prev: block,
                        next: null,
                    });
                }
            }
            // Attempt to wrap the block
            return (_jsxs(Fragment, { children: [spaceBefore, _jsx(BlockErrorBoundary, { block: block, blockContext: ctx, children: blockNode }), spaceAfter] }, block.slug + "_" + index));
        });
    }, [blocks, props.wrapBlock, props.spacer]);
    if (env.admin) {
        return _jsx(ReadonlyInlineEditingProvider, { children: layout });
    }
    return _jsx(Fragment, { children: layout });
});
ContentBlocks.displayName = "ContentBlocks";
export function SingleContentBlock(props) {
    const { block } = props;
    const Component = useBlockComponent(block);
    if (!Component) {
        if (block.innerHTML) {
            return _jsx("span", { style: { display: "contents" }, dangerouslySetInnerHTML: { __html: block.innerHTML } });
        }
        else {
            return null;
        }
    }
    block.props = block.props || {};
    // Apply the block style class name
    // @ts-ignore
    block.props.blockStyle = block.class?.replace("is-style-", "");
    let result = (_jsx(InlineEditingContextProvider, { block: [block.blockName, block.props], values: block.inline, innerBlocks: block.innerBlocks, index: props.index ?? 0, children: _jsx(Component, { ...block.props, innerHTML: block.innerHTML, 
            // innerBlocks={block.innerBlocks}
            children: Array.isArray(block.innerBlocks) && block.innerBlocks.length ? (_jsx(ContentBlocks, { blocks: block.innerBlocks })) : null }) }));
    if (!env.client && env.serverless) {
        const router = useRouter();
        if (router.isAI) {
            result = _jsx("div", { "data-block-type": props.block.slug, children: result });
        }
    }
    return result;
}
function useBlockComponent(block) {
    return blockManifestReader.value[block.blockName];
}
